<template>
  <span
    v-tooltip="{
      content: dataUrl,
      placement: 'top',
      onShow: () => isNotFound && !isMobile,
    }"
    class="beautified-link pt hover:bg-important-hover"
    @click.exact="openLink"
    @dragstart="dragStartHandler"
  >
    <a
      :href="newUrl"
      data-internal-link="true"
      @mousedown="anchorMousedown"
    >
      <span class="inline-flex w-0">&nbsp;</span>
      <ResourceIcon
        :src="resourceIcon.src || resourceIcon.icon || dataIconSrc"
        :color="resourceIcon.color || dataIconColor"
        :type="resourceIcon.type || dataIconType"
        class="beautified-link--link-icon"
        :img-style="{ transform: 'translateY(2.5px)' }"
      />
      <ResourceIcon
        v-if="[epicType, 'project'].includes(resourceType)"
        :src="additionalIcon.src || additionalIcon.icon || dataAdditionalIconSrc"
        :color="additionalIcon.color || dataAdditionalIconColor"
        :type="additionalIcon.type || dataAdditionalIconType"
        class="beautified-link--link-icon"
        :img-style="{ transform: 'translateY(2.5px)' }"
      />
      <span class="beautified-link--label"
        ><span>{{ name }}</span></span
      >
      <span class="inline-flex">&nbsp;</span>
    </a>
  </span>
</template>
<script>
import customIconsMixin from '@/mixins/customIconsMixin';
import constants from '@/utilities/constants';
import cardStatuses from '@/mixins/cardStatuses';
import getDashedTitle from '@/utilities/getDashedTitle';
import EventBus from '@/utilities/eventBus';
import ResourceIcon from '@/components/elements/common/ResourceIcon';
import getCardStatusIcon from '@/mixins/getCardStatusIcon';
import { ResourceType } from '@/types/resources';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import useSprintStateIcon from '@/utilities/composables/useSprintStateIcon';
import { getSprintListKey } from '@/utilities/sprintKeys';

export default {
  name: 'InternalLinkComponent',
  components: {
    ResourceIcon,
  },
  mixins: [customIconsMixin, cardStatuses, getCardStatusIcon],
  props: {
    dataUrl: {
      type: String,
      required: true,
      default: '',
    },
    name: {
      type: String,
      required: true,
      default: '',
    },

    dataIconColor: {
      type: String,
      required: false,
      default: '',
    },

    dataIconSrc: {
      type: String,
      required: false,
      default: '',
    },

    dataIconType: {
      type: String,
      required: false,
      default: '',
    },

    dataAdditionalIconSrc: {
      type: String,
      required: false,
      default: '',
    },

    dataAdditionalIconColor: {
      type: String,
      required: false,
      default: '',
    },

    dataAdditionalIconType: {
      type: String,
      required: false,
      default: '',
    },

    dataGetResource: {
      type: Boolean,
      required: false,
      default: false,
    },

    editorId: {
      type: String,
      required: true,
      default: '',
    },
  },
  emits: ['updateAttrs'],
  setup() {
    const { sprintStateIcon } = useSprintStateIcon();
    return { sprintStateIcon };
  },
  data() {
    return {
      epicType: ResourceType.Epic,
      additionalIcon: {},
      linkName: this.name,
      resourceIcon: {},
      newUrl: this.dataUrl,
      getResourceTimeout: null,
      oldResourceData: null,
    };
  },
  computed: {
    isAnyCardDisplayedInDockedLayout() {
      return this.$store.getters.getIsAnyCardDisplayedInLayout(this.$constants.docked);
    },
    isProjectDeleted() {
      return this.getTrashProjects.some((project) => project.id === this.deletedResourceProjectId);
    },
    deletedResourceProjectId() {
      return [ResourceType.Project, ResourceType.Space].includes(this.type)
        ? this.resourceId
        : this[this.resourceType]?.project_id || '';
    },

    userTeams() {
      return this.$store.getters.getUser.teams || [];
    },
    teamId() {
      const team = this.userTeams.find((t) => t.sub_domain === this.subdomain);
      if (!team) return '';
      return team.id || '';
    },
    board() {
      return this.$store.getters.getFlatBoards[this.resourceId] || {};
    },
    getFlatBoards() {
      return this.$store.getters.getFlatBoards;
    },
    projects() {
      return this.$store.getters.getTeamProjects || [];
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    resource() {
      return this.newUrl.split('://')[1];
    },
    subdomain() {
      return this.resource.split('/')[1] || '';
    },
    resourceData() {
      return this.resource.split('/')[2] || '';
    },
    resourceType() {
      return this.resourceData.split('-')[0] || '';
    },
    isNewPageBoard() {
      const newPageBoard = this.resource.split('/')[3] || '';
      return newPageBoard === 'newpage' || newPageBoard === 'newboard';
    },
    resourceId() {
      return this.resourceData.split('-')[1] || '';
    },
    internalLinkRoutePath() {
      return `/${this.subdomain}/${this.resourceData}${
        this.resource.split('/')[3] ? this.resource.split('/')[3] : ''
      }`;
    },
    tabName() {
      return this.resource.split('/')[3] || '';
    },
    isSameTeamSlug() {
      return this.subdomain === this.teamSlug;
    },
    resourceName() {
      return this.linkName || this.dataUrl;
    },
    favouritesOpened() {
      return this.$store.getters.getFavouritesOpened;
    },

    lists() {
      return this.$store.getters.lists;
    },
    card() {
      return this.cards[this.resourceId] || {};
    },
    page() {
      return this.$store.getters.getPageById(this.resourceId);
    },
    project() {
      return this.$store.getters.getTeamProjectById(this.resourceId);
    },
    getCurrentRouteFullPath() {
      return this.$store.getters.getCurrentRoute.fullPath;
    },
    cards() {
      return this.$store.getters.getCards;
    },
    getTrashProjects() {
      return this.$store.getters.getTrashProjects;
    },
    isNotFound() {
      return this.linkName === this.translate('notFound');
    },
    notFoundUrl() {
      const domain = this.dataUrl.split('://')[1];
      const workspace = domain.split('/')[1];
      const resourceData = domain.split('/')[2];
      return `/${workspace}/${resourceData}`;
    },

    user() {
      return this.$store.getters.getUser;
    },
  },
  watch: {
    isProjectDeleted(newVal) {
      if (newVal) {
        this.deletedResource();
      }
    },
  },

  mounted() {
    this.oldResourceData = {
      name: this.name,
      dataUrl: this.dataUrl,
      icon: {
        src: this.dataIconSrc,
        color: this.dataIconColor,
        type: this.dataIconType || null,
      },
    };

    this.getResourceTimeout = setTimeout(
      () => {
        this.getResourceData();
      },
      this.dataGetResource ? 0 : 1000
    );
  },

  unmounted() {
    clearTimeout(this.getResourceTimeout);
  },

  methods: {
    deletedResource() {
      this.linkName = this.translate('notFound');
      this.resourceIcon = {
        icon: 'x',
        color: 'var(--iconDefault)',
        type: 'icon',
      };
      this.updateLinkAttributes();
    },
    updateUrl() {
      const route = `/${this.teamSlug}/${this.resourceType}-${this.resourceId}-${getDashedTitle(
        this.resourceName
      )}`;
      const { hostname, protocol, hash, search } = new URL(this.dataUrl);
      this.newUrl = `${protocol}//${hostname}${route}${hash}/${search}`;
    },
    getResourceData() {
      switch (this.resourceType) {
        case 'project':
        case 'epic':
          this.getEpic();
          this.updateUrl();
          break;

        case 'card':
        case 'sprintcard':
          this.getCard();
          this.updateUrl();
          break;

        case 'page':
          this.getPage();
          this.updateUrl();
          break;

        case 'board':
          this.getBoard();
          this.updateUrl();
          break;

        case 'sprint':
          this.getSprint();
          this.updateUrl();
          break;

        case 'space':
          this.getProject();
          break;
        case 'my':
        case 'member':
        case 'note':
          this.handleUserWorkLink();
          break;
        default:
          break;
      }
    },
    getProject() {
      const { resourceId, teamId } = this;
      if (this.project == null) {
        this.deletedResource();
        return;
      }
      if (this.project && this.isSameTeamSlug) {
        this.linkName = this.project.title;
        this.resourceIcon = this.project.icon;
        this.updateLinkAttributes();
        this.updateAttributes({ 'data-icon-type': this.resourceIcon.type });
      } else {
        this.$store
          .dispatch('fetchProjectData', {
            teamId,
            resourceId,
          })
          .then((data) => {
            if (data.title) {
              this.linkName = data.title;
            }
            if (data.icon) {
              this.resourceIcon = data.icon;
            }
            this.updateLinkAttributes();
            this.updateAttributes({ 'data-icon-type': this.resourceIcon.type });
          });
      }
    },
    getCard() {
      const { teamId, resourceId } = this;
      this.$store
        .dispatch('fetchCard', { cardId: resourceId, teamId })
        .then((card) => {
          const { status, list_color } = card;
          this.linkName = card.title;
          if (this.isProjectDeleted) {
            this.deletedResource();
            return;
          }
          if (status && list_color) {
            this.cardStatus(status, 'resourceIcon', list_color);
            this.updateLinkAttributes();
          } else {
            const listId = card.sprint_id
              ? getSprintListKey(card.project_id, card.sprint_id, card.list_id)
              : card.list_id;
            this.getListAndUpdateIconSrcAndColor(listId);
          }
        })
        .catch(() => {
          this.deletedResource();
        });
      if (this.isProjectDeleted) {
        this.deletedResource();
        return;
      }
    },
    getSprint() {
      const { resourceId } = this;
      const [projectId, sprintId] = resourceId.split('s');

      this.$store.dispatch('getSprint', { sprintId, projectId }).then(({ sprint }) => {
        this.linkName = sprint.title;
        if (this.isProjectDeleted) {
          this.deletedResource();
          return;
        }
        this.resourceIcon = this.sprintStateIcon(sprint.state);
        this.updateLinkAttributes();
      });
    },
    getBoard() {
      const { teamId, resourceId } = this;
      if (!this.$isBoardFullyLoaded(this.board)) {
        this.$store
          .dispatch('fetchBoard', { boardId: resourceId, teamId })
          .then(({ board }) => {
            if (this.isProjectDeleted) {
              this.deletedResource();
              return;
            }
            this.linkName = board.title;
            this.setBoardIcon();
            this.updateLinkAttributes();
          })
          .catch(() => {
            this.deletedResource();
          });
      } else {
        if (this.isProjectDeleted) {
          this.deletedResource();
          return;
        }
        this.$nextTick(() => {
          this.linkName = this.board.title;
          this.setBoardIcon();
          this.updateLinkAttributes();
        });
      }
    },
    setBoardIcon() {
      this.resourceIcon = {
        icon: this.$store.getters.getCardsLayoutForBoard(this.resourceId) || 'board',
      };
    },
    getEpic() {
      const { teamId, resourceId } = this;
      this.$store
        .dispatch('fetchCard', { cardId: resourceId, teamId, type: ResourceType.Epic })
        .then((epic) => {
          const { status, list_color } = epic;
          this.linkName = epic.title;
          if (this.isProjectDeleted) {
            this.deletedResource();
            return;
          }
          this.updateAdditionalIconAttributes(
            epic.icon?.src || this.$constants.epicDefaultIcon,
            epic.icon?.color,
            epic.icon?.type
          );
          if (status && list_color) {
            this.cardStatus(status, 'resourceIcon', list_color, true);
            this.updateLinkAttributes();
          } else {
            const listId = epic.list_id;
            this.getListAndUpdateIconSrcAndColor(listId, true);
          }
        })
        .catch(() => {
          this.deletedResource();
        });
    },
    getPage() {
      const { teamId, resourceId } = this;
      if (this.isProjectDeleted) {
        this.deletedResource();
        return;
      }

      if (this.page) {
        this.setPageIcon();
      }

      this.$store
        .dispatch('fetchPageData', { teamId, resourceId })
        .then((data) => {
          this.linkName = data.title;
          if (!this.linkName) this.linkName = this.translate('untitledPage');
          if (this.isProjectDeleted) {
            this.deletedResource();
            return;
          }

          this.setPageIcon();
          this.updateLinkAttributes();
          this.updateAttributes({ 'data-icon-type': this.resourceIcon.type });
        })
        .catch(() => {
          this.deletedResource();
        });
    },
    setPageIcon() {
      this.resourceIcon = {
        icon:
          this.page.icon?.src ||
          (this.linkName === this.translate('untitledPage') ? 'file' : 'page'),
        color: this.page.icon?.color,
        type: this.page.icon?.type,
      };
    },
    dragStartHandler(e) {
      e.preventDefault();
    },

    setCurrentProjectAndBoardId(projectId, boardId) {
      this.$store.dispatch('setCurrentBoardId', boardId);
      this.$store.dispatch('setCurrentProjectId', projectId);
    },

    fetchBoardSetCurrentProjectId() {
      if (!this.$isBoardFullyLoaded(this.board)) {
        this.$store.dispatch('fetchBoard', { boardId: this.resourceId }).then(({ board }) => {
          this.setCurrentProjectAndBoardId(board.project_id, board.id);
        });
      } else {
        this.setCurrentProjectAndBoardId(this.board.project_id, this.board.id);
      }
    },

    fetchCardAndBoard() {
      if (this.$store.getters.getIsCardDisplayed(this.resourceId)) return;

      this.$store.dispatch('fetchCard', { cardId: this.resourceId }).then((card) => {
        if (!this.$isBoardFullyLoaded(this.board) && card.board_id) {
          this.$store.dispatch('fetchBoard', { boardId: card.board_id });
        }
      });
    },

    anchorMousedown(e) {
      if (e.button !== 2) {
        e.preventDefault();
        e.stopPropagation();
      }
    },

    checkForCardCommentLink() {
      const internalLinkHref = new URL(this.dataUrl);
      const commentId = internalLinkHref.searchParams?.get('commentId');
      const parentCommentId = internalLinkHref.searchParams?.get('parentCommentId');
      if (commentId && this.resourceType === ResourceType.Card) {
        EventBus.$emit('scrollToNewComment', {
          cardId: this.resourceId,
          commentId,
          parentCommentId,
        });
        history.pushState({ commentId }, null, this.internalLinkRoutePath);
        return true;
      }
      return false;
    },

    openLink(e) {
      e.stopPropagation();
      if (this.isSameTeamSlug) {
        e.preventDefault();
        if (this.isNotFound) {
          this.$store.dispatch('itemNotFound', true);
          this.$router.push(this.notFoundUrl);
          if (this.resourceType === 'space') {
            this.$store.dispatch('setCurrentProjectId', this.deletedResourceProjectId);
          }
          return;
        }
        const notification = {
          message: this.$capitalizeFirstLetter(
            this.translate('alreadyOpen', { resource: this.resourceType })
          ),
          icon: 'info',
          duration: 4000,
        };
        if ([constants.projectSlug].includes(this.resourceType)) {
          const project = this.$store.getters.getTeamProjectById(this.resourceId);
          if (!project) return;
          this.setProject(project);
        }
        if (this.resourceType === ResourceType.Board || this.resourceType === ResourceType.Sprint) {
          this.fetchBoardSetCurrentProjectId();
        }
        if (
          this.resourceType === ResourceType.Card ||
          this.resourceType === ResourceType.SprintCard ||
          this.resourceType === ResourceType.Epic ||
          this.resourceType === 'project'
        ) {
          this.fetchCardAndBoard();
          this.$store.dispatch('setDisplayedCards', [this.resourceId]);
          const route = new URL(window.location.href);

          if (this.checkForCardCommentLink()) {
            return;
          }

          if (
            this.internalLinkRoutePath.startsWith(route.pathname) &&
            this.isAnyCardDisplayedInDockedLayout
          ) {
            this.$store.dispatch('addUiNotification', notification);
            return;
          }
          const resolvedRoute = this.$router.resolve(this.internalLinkRoutePath);
          this.$router.push({
            path: this.internalLinkRoutePath,
            query: { ...(resolvedRoute.query || {}), userNavigated: true },
          });
          return;
        } else if (!this.isAnyCardDisplayedInDockedLayout) {
          this.$store.dispatch('setDisplayedCards');
        }
        if (this.favouritesOpened) {
          this.$store.dispatch('setFavouritesOpened', false);
          this.$store.dispatch('setOpenedFavouriteItem', {});
        }
        const url = new URL(this.newUrl);
        // if internal link is same as current route, only scroll to anchor
        // check if internalLinkRoutePath is a substring of current route without routes last characther because of trailing slash
        if (
          this.internalLinkRoutePath.startsWith(this.$route.path.slice(0, -1)) ||
          url.pathname.startsWith(this.getCurrentRouteFullPath)
        ) {
          if (this.isAnyCardDisplayedInDockedLayout) {
            this.$store.dispatch('addUiNotification', notification);
          }
          // if there is commentId or parentCommentId in url, emit scroll to comment
          if (url.searchParams.has('commentId') || url.searchParams.has('parentCommentId')) {
            const commentId = url?.searchParams?.get('commentId');
            const parentCommentId = url?.searchParams?.get('parentCommentId');
            EventBus.$emit('scrollToPageComment', commentId, parentCommentId);
          }

          // Prevent adding multiple history states if same url
          if (this.$route.fullPath !== this.internalLinkRoutePath) {
            this.$router.push(this.internalLinkRoutePath).then(() => {
              history.replaceState(history.state, null, this.internalLinkRoutePath);
            });
          }
          if (url.hash === '') return;
          EventBus.$emit('scrollToAnchor', { editorId: this.editorId, url: this.newUrl });
          return;
        } else {
          if (this.resourceType === ResourceType.Page) {
            this.$store.dispatch('setCurrentPageID', this.page.id);
            this.$store.dispatch('setPage', this.page);
          }
          this.$router.push(this.internalLinkRoutePath);
        }
      }
    },
    setProject(project) {
      this.$store.dispatch('setCurrentProjectId', project.id);
      this.$store.dispatch('displayInfoSidebar', false);
      this.$store.dispatch('loadProjectDetails', project.id);

      if (this.tabName === 'pages') {
        this.$store.dispatch('loadPages');
      }
    },

    updateAttributes(newAtrributes) {
      this.$emit('updateAttrs', newAtrributes);
    },

    updateAdditionalIconAttributes(src, color = null, type = null) {
      this.additionalIcon = {
        src,
        color,
        type,
      };
      this.updateAttributes({
        'data-additional-icon-src': src,
        'data-additional-icon-color': color || null,
        'data-additional-icon-type': type || null,
      });
    },
    updateLinkAttributes() {
      const newData = {
        name: this.linkName,
        dataUrl: this.dataUrl,
        icon: {
          src: this.resourceIcon.icon || this.resourceIcon.src,
          color: this.resourceIcon.color,
          type: this.resourceIcon.type || null,
        },
      };

      if (JSON.stringify(this.oldResourceData) === JSON.stringify(newData)) {
        return;
      }

      this.updateAttributes({
        'data-icon-src': this.resourceIcon.icon || this.resourceIcon.src,
        'data-icon-color': this.resourceIcon.color,
        name: this.linkName,
        'data-get-resource': null,
      });
    },

    getList(listId) {
      if (this.lists[listId]) {
        return Promise.resolve(this.lists[listId]);
      } else {
        return this.$store
          .dispatch('getList', {
            teamId: this.teamId,
            listId,
          })
          .then(({ list }) => {
            this.$store.dispatch('setBoardListsFlat', [list]);
            return list;
          })
          .catch();
      }
    },

    getListAndUpdateIconSrcAndColor(listId, isEpic = false) {
      const isSprint = !!this.card.sprint_id;
      if (this.lists[listId]) {
        const listColor = isSprint
          ? getCardStatus(this.lists[listId].behavior)?.color || '#000'
          : this.lists[listId].color;
        this.cardStatus(this.lists[listId].behavior, 'resourceIcon', listColor, isEpic);
        this.updateLinkAttributes();
        return;
      }
      if (!isSprint) {
        this.getList(listId).then((list) => {
          if (!list) return;
          this.cardStatus(list.behavior, 'resourceIcon', list.color, isEpic);
          this.updateLinkAttributes();
        });
      }
    },

    handleUserWorkLink() {
      const user =
        this.resourceType === ResourceType.Note
          ? this.user
          : this.$store.getters.getMemberById(this.resourceId);

      const otherUserFirstName = user?.first_name || user?.display_name;
      const userFirstName = this.resourceType === 'my' ? this.user.first_name : otherUserFirstName;
      const name = this.translate('usersWork', { name: userFirstName });
      this.updateAttributes({ 'data-icon-src': 'my-work', name });
    },
  },
};
</script>

<style lang="scss">
.beautified-link {
  box-shadow: 0px 0px 0px 1px inset var(--dividers);
  @apply bg-important-bg text-text-default;

  @apply rounded
    py-0
    inline
    grow-0
    overflow-hidden
    text-ellipsis;

  a {
    color: var(--textDefault) !important;
    font-weight: normal !important;
    text-decoration: none;
    white-space: nowrap;

    @apply font-normal
      inline
      no-underline
      text-ellipsis
      overflow-hidden
      items-center;

    em {
      background: $gray400;
      min-width: 16px;
      min-height: 16px;

      @apply h-4
        w-4;
    }

    &:hover {
      text-decoration: none !important;
    }
  }

  &--label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @apply pr-0.5 inline-flex grow-0;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &--link-icon {
    mask-size: 16px !important;
    mask-position: center;
    @apply ml-0.5 mr-1;
  }

  .beautified-link--label {
    max-width: 90%;
  }
}
</style>
